/**
 * Approvals — polls GET /api/approvals every 10s.
 *
 * - operator|fleet_owner: Approve / Reject buttons + optional grade (-5..+5 or skip).
 * - viewer: read-only notice, no action buttons.
 * - 409 (already responded): graceful refetch instead of crash.
 * - Toggle to show all statuses (responded included).
 *
 * The api client handles silent token refresh transparently. An UnauthorizedError
 * reaching this component means the console session is truly gone.
 */
import { useEffect, useState, useCallback } from 'react'
import { api, UnauthorizedError, ForbiddenError } from '../api/client'
import type { Approval } from '../api/client'
import { useAuth } from '../context/AuthContext'
import { relativeTime, countdown } from '../utils/relativeTime'
import { useToast, ToastContainer } from '../utils/toast'

const POLL_INTERVAL_MS = 10_000

const GRADES = [-5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5]

function statusBadge(status: string): React.ReactNode {
  const map: Record<string, string> = {
    pending:   'bg-amber-100 text-amber-800',
    responded: 'bg-green-100 text-green-800',
    expired:   'bg-gray-100 text-gray-500',
    rejected:  'bg-red-100 text-red-800',
  }
  const cls = map[status] ?? 'bg-gray-100 text-gray-600'
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {status}
    </span>
  )
}

interface ApprovalCardProps {
  approval: Approval
  canAct: boolean
  onRespond: (id: string, action: 'approve' | 'reject', grade: number | null) => Promise<void>
  responding: boolean
}

function ApprovalCard({ approval, canAct, onRespond, responding }: ApprovalCardProps) {
  const [grade, setGrade] = useState<number | null>(null)
  const [skipGrade, setSkipGrade] = useState(false)

  const effectiveGrade = skipGrade ? null : grade

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">{approval.title || '—'}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {statusBadge(approval.status)}
            <span className="text-xs text-gray-400 font-mono">{approval.agent_id}</span>
            <span className="text-xs text-gray-400">{approval.action_type}</span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs text-gray-500">{relativeTime(approval.created_at)}</p>
          {approval.expires_at && approval.status === 'pending' && (
            <p className="text-xs text-amber-600 mt-0.5">expires {countdown(approval.expires_at)}</p>
          )}
        </div>
      </div>

      {/* Body */}
      {approval.body && (
        <pre className="text-xs text-gray-700 bg-gray-50 border border-gray-100 rounded p-3 whitespace-pre-wrap break-words max-h-40 overflow-auto font-mono">
          {approval.body}
        </pre>
      )}

      {/* Responded info */}
      {approval.status !== 'pending' && approval.responded_by && (
        <p className="text-xs text-gray-500">
          Responded by <strong>{approval.responded_by}</strong> {relativeTime(approval.responded_at)}
          {approval.grade !== null ? ` · grade ${approval.grade}` : ''}
        </p>
      )}

      {/* Actions (operator / fleet_owner only, pending only) */}
      {canAct && approval.status === 'pending' && (
        <div className="border-t border-gray-100 pt-3 space-y-3">
          {/* Grade selector */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs text-gray-500 font-medium">Grade (optional):</span>
            <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
              <input
                type="checkbox"
                checked={skipGrade}
                onChange={e => setSkipGrade(e.target.checked)}
                className="rounded"
              />
              Skip
            </label>
            {!skipGrade && (
              <select
                value={grade ?? ''}
                onChange={e => setGrade(e.target.value === '' ? null : Number(e.target.value))}
                className="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
              >
                <option value="">— no grade —</option>
                {GRADES.map(g => (
                  <option key={g} value={g}>{g > 0 ? `+${g}` : g}</option>
                ))}
              </select>
            )}
          </div>

          {/* Buttons */}
          <div className="flex gap-2">
            <button
              disabled={responding}
              onClick={() => { void onRespond(approval.id, 'approve', effectiveGrade) }}
              className="flex-1 px-3 py-1.5 rounded bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Approve
            </button>
            <button
              disabled={responding}
              onClick={() => { void onRespond(approval.id, 'reject', effectiveGrade) }}
              className="flex-1 px-3 py-1.5 rounded bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Reject
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export function Approvals() {
  const { user } = useAuth()
  const { toasts, addToast } = useToast()
  const canAct = user.fleet_role === 'operator' || user.fleet_role === 'fleet_owner'

  const [approvals, setApprovals] = useState<Approval[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [showAll, setShowAll] = useState(false)
  const [responding, setResponding] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [secsAgo, setSecsAgo] = useState(0)

  const fetchApprovals = useCallback(async (status?: string) => {
    try {
      const res = await api.getApprovals(status)
      setApprovals(res.approvals)
      setLastUpdated(new Date())
      setSecsAgo(0)
      setError(null)
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        setError('Session expired. Please re-authenticate.')
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load approvals.')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const status = showAll ? 'all' : 'pending'
    void fetchApprovals(status)
    const interval = setInterval(() => { void fetchApprovals(status) }, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [fetchApprovals, showAll])

  useEffect(() => {
    if (!lastUpdated) return
    const t = setInterval(() => {
      setSecsAgo(Math.floor((Date.now() - lastUpdated.getTime()) / 1000))
    }, 1000)
    return () => clearInterval(t)
  }, [lastUpdated])

  const handleRespond = useCallback(async (
    id: string,
    action: 'approve' | 'reject',
    grade: number | null,
  ) => {
    setResponding(id)
    // Optimistic remove for pending view.
    if (!showAll) {
      setApprovals(prev => prev.filter(a => a.id !== id))
    }
    try {
      await api.respondApproval(id, { action, grade })
      addToast(`Approval ${action === 'approve' ? 'approved' : 'rejected'}.`, 'success')
      // Refetch to get updated state.
      void fetchApprovals(showAll ? 'all' : 'pending')
    } catch (err) {
      if (err instanceof ForbiddenError) {
        addToast('Permission denied — operator or fleet_owner required.', 'error')
      } else if (err instanceof Error && err.message.startsWith('HTTP 409')) {
        addToast('Already responded — refreshing.', 'info')
      } else {
        addToast(err instanceof Error ? err.message : 'Respond failed.', 'error')
      }
      // Restore optimistic remove by refetching.
      void fetchApprovals(showAll ? 'all' : 'pending')
    } finally {
      setResponding(null)
    }
  }, [showAll, fetchApprovals, addToast])

  const pendingCount = approvals.filter(a => a.status === 'pending').length

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Approvals</h1>
          <p className="text-sm text-gray-500 mt-1">
            {showAll ? 'All approvals.' : `Pending approvals${pendingCount > 0 ? ` (${pendingCount})` : '.'}`}
          </p>
        </div>
        <div className="flex items-center gap-4">
          {lastUpdated && (
            <p className="text-xs text-gray-400">Updated {secsAgo}s ago</p>
          )}
          <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showAll}
              onChange={e => setShowAll(e.target.checked)}
              className="rounded"
            />
            Show responded
          </label>
        </div>
      </div>

      {/* Viewer notice */}
      {!canAct && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
          View only — ask an operator or fleet owner to approve or reject.
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading && !error && (
        <div className="text-sm text-gray-400 animate-pulse">Loading approvals...</div>
      )}

      {!loading && !error && (
        <>
          {approvals.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 px-5 py-10 text-center">
              <p className="text-sm text-gray-400">
                {showAll ? 'No approvals found.' : 'No pending approvals.'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {approvals.map(approval => (
                <ApprovalCard
                  key={approval.id}
                  approval={approval}
                  canAct={canAct}
                  onRespond={handleRespond}
                  responding={responding === approval.id}
                />
              ))}
            </div>
          )}
        </>
      )}

      <ToastContainer toasts={toasts} />
    </div>
  )
}
